import React, { useMemo, useState, useEffect } from 'react';
import './DashboardTab.css';
import { format, parseISO, isWithinInterval } from 'date-fns';

const DashboardTab = ({ data, settings, onDeleteEntry }) => {
  const [startDate, setStartDate] = useState(() => {
    return format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd');
  });
  
  const [endDate, setEndDate] = useState(() => {
    return format(new Date(), 'yyyy-MM-dd');
  });
  
  const [filterGroup, setFilterGroup] = useState('All');
  const [filterTimeGroup, setFilterTimeGroup] = useState('All');

  // Update date range when data loads
  useEffect(() => {
    if (data && data.length > 0) {
      const dates = data.map(d => new Date(d.date));
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));
      setStartDate(format(minDate, 'yyyy-MM-dd'));
      setEndDate(format(maxDate, 'yyyy-MM-dd'));
    }
  }, [data]);

  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    let filtered = data.filter(entry => {
      const entryDate = new Date(entry.date);
      const start = parseISO(startDate);
      const end = parseISO(endDate);
      // Add one day to end date to make it inclusive (create new date object)
      const endInclusive = new Date(end);
      endInclusive.setHours(23, 59, 59, 999);
      const isWithin = isWithinInterval(entryDate, { start, end: endInclusive });
      return isWithin;
    });

    if (filterGroup !== 'All') {
      filtered = filtered.filter(entry => entry.group === filterGroup);
    }

    if (filterTimeGroup !== 'All') {
      filtered = filtered.filter(entry => entry.time_group === filterTimeGroup);
    }

    return filtered;
  }, [data, startDate, endDate, filterGroup, filterTimeGroup]);

  const summary = useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return null;
    }

    return {
      totalRevenue: filteredData.reduce((sum, item) => sum + (item.daily_revenue || 0), 0),
      averageDailyRevenue: filteredData.reduce((sum, item) => sum + (item.daily_revenue || 0), 0) / filteredData.length,
      totalJournal: filteredData.reduce((sum, item) => sum + (item.total_journal || 0), 0),
      averageDifference: filteredData.reduce((sum, item) => sum + (item.difference || 0), 0) / filteredData.length,
      entriesCount: filteredData.length,
      startDate: filteredData.length > 0 ? new Date(Math.min(...filteredData.map(d => new Date(d.date)))) : null,
      endDate: filteredData.length > 0 ? new Date(Math.max(...filteredData.map(d => new Date(d.date)))) : null
    };
  }, [filteredData]);

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [filteredData]);

  const setDateRangeToData = () => {
    if (data.length > 0) {
      const dates = data.map(d => new Date(d.date));
      setStartDate(format(new Date(Math.min(...dates)), 'yyyy-MM-dd'));
      setEndDate(format(new Date(Math.max(...dates)), 'yyyy-MM-dd'));
    }
  };

  const clearFilters = () => {
    if (data.length > 0) {
      const dates = data.map(d => new Date(d.date));
      setStartDate(format(new Date(Math.min(...dates)), 'yyyy-MM-dd'));
      setEndDate(format(new Date(Math.max(...dates)), 'yyyy-MM-dd'));
    }
    setFilterGroup('All');
    setFilterTimeGroup('All');
  };

  return (
    <div className="dashboard-tab">
      <h2>Revenue Dashboard</h2>

      {summary && (
        <div className="summary-cards">
          <div className="summary-card card-revenue">
            
            <div className="card-content">
              <div className="card-label">Total Revenue</div>
              <div className="card-value">
                {summary.totalRevenue.toFixed(2)}{settings.currency}
              </div>
            </div>
          </div>

          <div className="summary-card card-average">
            
            <div className="card-content">
              <div className="card-label">Average Daily Revenue</div>
              <div className="card-value">
                {summary.averageDailyRevenue.toFixed(2)}{settings.currency}
              </div>
            </div>
          </div>

          <div className="summary-card card-journal">
            
            <div className="card-content">
              <div className="card-label">Total Journal</div>
              <div className="card-value">
                {summary.totalJournal.toFixed(2)}{settings.currency}
              </div>
            </div>
          </div>

          <div className={`summary-card card-difference ${summary.averageDifference >= 0 ? 'card-positive' : 'card-negative'}`}>
           
            <div className="card-content">
              <div className="card-label">Average Difference</div>
              <div className={`card-value ${summary.averageDifference >= 0 ? 'positive' : 'negative'}`}>
                {summary.averageDifference.toFixed(2)}{settings.currency}
              </div>
            </div>
          </div>

          <div className="summary-card card-count">
            
            <div className="card-content">
              <div className="card-label">Entries Count</div>
              <div className="card-value">{summary.entriesCount}</div>
            </div>
          </div>

          <div className="summary-card card-date">
           
            <div className="card-content">
              <div className="card-label">Date Range</div>
              <div className="card-value small">
                {summary.startDate && format(summary.startDate, 'yyyy-MM-dd')} to{' '}
                {summary.endDate && format(summary.endDate, 'yyyy-MM-dd')}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="data-table-container">
        <div className="table-header">
          <h3>Recent Entries</h3>
          <div className="table-actions">
            <button className="btn-reset" onClick={clearFilters} title="Clear all filters">
              Clear Filters
            </button>
          </div>
        </div>

        <div className="filters-panel">
          <div className="filter-group">
            <label>From:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>To:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Group:</label>
            <select
              value={filterGroup}
              onChange={(e) => setFilterGroup(e.target.value)}
            >
              <option value="All">All</option>
              {settings.groups.map(group => (
                <option key={group} value={group}>{group}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Time Group:</label>
            <select
              value={filterTimeGroup}
              onChange={(e) => setFilterTimeGroup(e.target.value)}
            >
              <option value="All">All</option>
              <option value="7am to 2pm">7am to 2pm</option>
              <option value="2pm to 10pm">2pm to 10pm</option>
            </select>
          </div>

          <div className="filter-group">
            <button className="btn-set-range" onClick={setDateRangeToData} title="Set date range to all data">
              Set to All Data
            </button>
          </div>
        </div>

        {sortedData.length === 0 ? (
          <div className="no-data">No entries yet. Add your first entry in the Data Entry tab.</div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Group</th>
                  <th>Time Group</th>
                  <th>Billet</th>
                  <th>Money</th>
                  <th>Total Calc</th>
                  <th>Font Caisse</th>
                  <th>Credit</th>
                  <th>Achat</th>
                  <th>Journal</th>
                  <th>Difference</th>
                  <th>Revenue</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((entry) => (
                  <tr key={entry._id || entry.id}>
                    <td>{format(new Date(entry.date), 'yyyy-MM-dd')}</td>
                    <td>{entry.group}</td>
                    <td>{entry.time_group || 'N/A'}</td>
                    <td>{entry.billet?.toFixed(2) || '0.00'}</td>
                    <td>{entry.money?.toFixed(2) || '0.00'}</td>
                    <td>{entry.total_calculated?.toFixed(2) || ((entry.billet || 0) + (entry.money || 0)).toFixed(2)}</td>
                    <td>{entry.font_caisse?.toFixed(2) || '0.00'}</td>
                    <td>{entry.total_credit?.toFixed(2) || '0.00'}</td>
                    <td>{entry.total_achat?.toFixed(2) || '0.00'}</td>
                    <td>{entry.total_journal?.toFixed(2) || '0.00'}</td>
                    <td className={entry.difference >= 0 ? 'positive' : 'negative'}>
                      <span>{entry.difference?.toFixed(2) || '0.00'}</span>
                    </td>
                    <td className="revenue">
                      <span>{entry.daily_revenue?.toFixed(2) || '0.00'}</span>
                    </td>
                    <td>
                      <button
                        className="btn-delete"
                        onClick={() => onDeleteEntry(entry._id || entry.id)}
                        title="Delete entry"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardTab;

